/* eslint-disable react/jsx-indent */
// jsx-indent disabled: the grid body is wrapped in <Collapse> (item 124 collapsible
// sections); the wrapper shifts nesting but the body keeps its original indent.
import React, { useMemo, useState } from 'react';
import { FiPlus } from 'react-icons/fi';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Center,
  Collapse,
  Flex,
  Heading, HStack, IconButton,
  Skeleton,
  Slide,
  Spacer,
  Stack,
  useColorModeValue,
} from '@chakra-ui/react';
import { CloseIcon } from '@chakra-ui/icons';
import { Form } from 'react-final-form';
import { QueryStatus, useQuery } from 'react-query';
import { useHistory } from 'react-router-dom';
import GridInstance from './GridInstance';
import PageHeader from './PageHeader';
import DocumentFilterBar, {
  applyStatusFilter, applyDateFilter, StatusFilterConfig, StatusFilterValue, DateFilterValue,
} from './DocumentFilterBar';
import SelectInputControl from './forms/SelectInput';
import BrandIcon from './BrandIcon';
import AuroraBackground from './dashboard/AuroraBackground';
import GlassGridPanel from './GlassGridPanel';
import { Project } from '../models/project';
import { getProjects } from '../hooks/useProjects';
import useCompanyFiscal from '../hooks/useCompanyFiscal';

// The page shell shared by all five document grids: Aurora header (glowing icon
// chip + gradient title + ADD), the glass panel around GridInstance, and the
// optional copy-to-project slide-down. Columns/per-type handlers stay per grid.

export interface CopyToProjectProps {
  isOpen: boolean;
  onClose: () => void;
  id: string;
  onSubmit: (values: { id: string, newProject: string }) => void;
}

interface DocumentGridShellProps {
  heading: string;
  icon: string;
  // omit to hide the ADD button — e.g. invoices, which are only ever created by
  // converting a work order / sales order (item 38)
  addPath?: string;
  // item 462a — modal adds (e.g. the subscription wizard): renders the same ADD
  // button but calls a handler instead of pushing a route
  onAdd?: () => void;
  status: QueryStatus;
  data?: any[];
  // the react-table column set for the built-in grid body; omit when passing a custom
  // `children` body (item 547 — the ledger migration renders a LedgerTable instead).
  columns?: any[];
  // item 547 — a custom body (e.g. LedgerTable). When provided it renders inside the same
  // header/collapse/ADD chrome INSTEAD of the built-in GridInstance + column filter bar, so
  // every list can move to the ledger grammar without losing the section shell.
  children?: React.ReactNode;
  copyTo?: CopyToProjectProps;
  rowHref?: (original: any) => string;
  // when provided, renders the shared status filter above the grid (item 70)
  statusFilter?: StatusFilterConfig;
  // the row date field the date-range filter targets (item 71); '' hides it
  dateField?: string;
  // item 124: render a header chevron + collapse the body (project view sections)
  collapsible?: boolean;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

const DocumentGridShell: React.FC<DocumentGridShellProps> = (props) => {
  const {
    heading, icon, addPath, onAdd, status, data, columns, children, copyTo, rowHref, statusFilter, dateField,
    collapsible, collapsed, onToggleCollapse,
  } = props;
  const history = useHistory();
  const [statusValue, setStatusValue] = useState<StatusFilterValue>('all');
  const [dateValue, setDateValue] = useState<DateFilterValue>('all');
  // visible row count reported by the grid (reflects its per-column text filters too)
  const [gridCount, setGridCount] = useState<number | undefined>(undefined);
  const { fiscal } = useCompanyFiscal();
  const copyToBg = useColorModeValue('white', 'rgba(17,21,28,0.92)');
  const glassBorder = useColorModeValue('gray.200', 'whiteAlpha.200');
  const { data: projects } = useQuery('projects', getProjects, { enabled: copyTo !== undefined });

  const filteredData = useMemo(() => {
    if (!Array.isArray(data)) return data;
    let rows = data;
    if (statusFilter) rows = applyStatusFilter(rows, statusValue);
    if (dateField) rows = applyDateFilter(rows, dateValue, dateField, fiscal?.yearStartMonth ?? 1);
    return rows;
  }, [data, statusFilter, statusValue, dateField, dateValue, fiscal]);
  const baseCount = Array.isArray(filteredData) ? filteredData.length : undefined;
  // prefer the grid's reported count (includes its per-column text filters)
  const count = gridCount ?? baseCount;

  // item 538 — collapsed project sections are slim; expanded/standalone stay roomy
  type Pad = number | { base: number; md: number };
  // item 732 (Chad) — density pass: trimmed the roomy standalone-grid padding
  let shellPt: Pad = { base: 3, md: 4 };
  let shellPb: Pad = { base: 4, md: 5 };
  if (collapsible) {
    // item 732 (Chad) — condensed collapsed project sections: slimmer so more fit on screen
    shellPt = collapsed ? 1 : { base: 2, md: 3 };
    shellPb = collapsed ? 1 : { base: 2, md: 3 };
  }

  return (
    <Box
      position="relative"
      mx={{ base: -4, md: -10 }}
      mb={collapsible ? 0 : { base: -4, md: -8 }}
      px={{ base: 4, md: 10 }}
      // item 538 — a COLLAPSED project section is a slim scannable bar (tight top/bottom);
      // expanded sections and standalone grid pages keep their roomier padding.
      pt={shellPt}
      pb={shellPb}
      // standalone grid pages fill the viewport so the aurora canvas reaches the
      // fold; project-view sections (collapsible) must size to their content so a
      // collapsed section shrinks to its header and every category stacks at the
      // top — otherwise each section reserves ~100vh and Collapse-all looks inert.
      minH={collapsible ? undefined : { base: 'auto', lg: 'calc(100vh - 160px)' }} // item 1063
    >
      <AuroraBackground />
      <Box position="relative" zIndex={1}>
        {/* item 353 — sticky is BACK (supersedes the 2026-06-28 revert of 313): Chad now
            wants every page title pinned below the dashboard; the backing that made him
            revert is fixed (glass + brand rule instead of the flat black box). */}
        <PageHeader
          /* item 381 — in the PROJECT VIEW (collapsible) this is a SECTION: compact
             header, no sticky (six pinning titles fought each other); standalone
             pages keep the large sticky page title. */
          sticky={!collapsible}
          dense={collapsible}
          // item 553 — in a collapsible SECTION, clicking the icon/title toggles it too
          // (not just the tiny chevron); standalone pages keep a static title.
          onTitleClick={collapsible ? onToggleCollapse : undefined}
          collapsed={collapsible ? collapsed : undefined}
          icon={<BrandIcon src={icon} color="currentColor" boxSize={collapsible ? '15px' : '18px'} />}
          title={heading}
          subtitle={count !== undefined ? `${count} ${count === 1 ? 'record' : 'records'}` : undefined}
          actions={(addPath !== undefined || onAdd !== undefined) ? (
            <Button
              aria-label={`Add ${heading}`}
              // item 732 (Chad) — xs in the condensed collapsed project sections; shared minW so
              // ADD/UPLOAD line up equal-width. The collapse chevron is now owned by PageHeader.
              size={collapsible ? 'xs' : 'md'}
              minW={collapsible ? '92px' : undefined}
              onClick={() => { if (onAdd) onAdd(); else if (addPath !== undefined) history.push(addPath); }}
            >
              <FiPlus style={{ marginRight: '6px' }} />
              {' '}
              ADD
            </Button>
          ) : undefined}
        />

        <Collapse in={!collapsed} animateOpacity>
        {/* item 547 — the built-in column filter bar is only for the GridInstance body; a
            custom (ledger) body brings its own search/filters. */}
        {!children && status === 'success' && data && (
          <DocumentFilterBar
            statusFilter={statusFilter}
            statusValue={statusValue}
            onStatusChange={setStatusValue}
            showDateFilter={!!dateField}
            dateValue={dateValue}
            onDateChange={setDateValue}
          />
        )}

        {/* item 406 — the shell's former inline glass copy is gone; GlassGridPanel is
            the ONE grid surface (they were documented as needing to match — now they
            can't drift) */}
        <GlassGridPanel>
          {(status === 'success' && data) && (children ?? (
            <GridInstance
              columns={columns ?? []}
              data={filteredData ?? data}
              rowHref={rowHref}
              enableColumnFilters
              onRowCountChange={setGridCount}
            />
          ))}
          {status === 'error'
            && (
            <Alert status="error" my={4} borderRadius="lg">
              <AlertIcon />
              Couldn&apos;t load this list. Check your connection and reload the page.
            </Alert>
            )}
          {(status === 'loading' || (status !== 'error' && !data))
            && (
            <Stack spacing={3}>
              <Skeleton key={1} height="20px" borderRadius="md" />
              <Skeleton key={2} height="20px" borderRadius="md" />
              <Skeleton key={3} height="20px" borderRadius="md" />
              <Skeleton key={4} height="20px" borderRadius="md" />
              <Skeleton key={5} height="20px" borderRadius="md" />
            </Stack>
            )}
        </GlassGridPanel>
        </Collapse>

        {copyTo !== undefined
        && (
        <Slide direction="top" in={copyTo.isOpen} unmountOnExit style={{ zIndex: 10 }}>
          <Center mt={4}>
            <Box
              maxW={{ base: '100vw', md: '80vw' }}
              w="full"
              bg={copyToBg}
              borderWidth="1px"
              borderColor={glassBorder}
              sx={{ backdropFilter: 'var(--bx-glass-panel)' }}
              boxShadow="2xl"
              borderRadius="2xl"
              overflow="hidden"
            >
              <HStack p={3}>
                <Heading fontSize="xl" fontWeight={600} fontFamily="body">
                  SELECT PROJECT
                </Heading>
                <Spacer />
                <IconButton variant="ghost" aria-label="back" onClick={copyTo.onClose} icon={<CloseIcon />} />
              </HStack>
              <Box p={4}>
                <Form
                  onSubmit={copyTo.onSubmit}
                  initialValues={{ id: copyTo.id }}
                  render={({ handleSubmit }) => (
                    <form onSubmit={handleSubmit}>
                      <SelectInputControl name="newProject" label="Project Name">
                        {projects?.index.map(
                          // eslint-disable-next-line max-len
                          (project: Project) => <option key={project.id} value={project.id}>{project.name}</option>,
                        )}
                      </SelectInputControl>
                      <Flex py={4} align="center" justify="center">
                        <Button type="submit">Save</Button>
                      </Flex>
                    </form>
                  )}
                />
              </Box>
            </Box>
          </Center>
        </Slide>
        )}
      </Box>
    </Box>
  );
};

DocumentGridShell.defaultProps = {
  data: undefined,
  columns: undefined,
  children: undefined,
  addPath: undefined,
  onAdd: undefined,
  copyTo: undefined,
  rowHref: undefined,
  statusFilter: undefined,
  dateField: 'date',
  collapsible: false,
  collapsed: false,
  onToggleCollapse: undefined,
};

export default DocumentGridShell;
